import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import React from 'react'

import { getCustomBreakdownId } from 'features/charts-v2/components/breakdown-control-utils'
import {
  type BreakdownSelection,
  isCustomBreakdown,
  isGroupableBreakdown,
  isStandardBreakdown,
} from 'features/charts-v2/components/breakdown-selection-utils'
import { BreakdownCard } from 'features/charts-v2/components/BreakdownCard'
import { BreakdownSelectHeader } from 'features/charts-v2/components/BreakdownSelectHeader'
import {
  type BreakdownOption,
  BREAKDOWNS,
  type CustomBreakdown,
  type TimeBucketOption,
} from 'features/charts-v2/types'
import { type FilterOptionSelect, FilterTypes } from 'features/filters/types'

export interface BreakdownControlProps {
  selectedBreakdowns: BreakdownSelection[]
  setSelectedBreakdowns: (breakdowns: BreakdownSelection[]) => void
  selectedTimeBucket: TimeBucketOption | null
  setSelectedTimeBucket: (timeBucket: TimeBucketOption | null) => void
  maxSelections?: number
  // Time bucket selections per breakdown (for when breakdown is "Time")
  timeBucketSelections?: Record<string, TimeBucketOption | null>
  setTimeBucketSelections?: (
    selections: Record<string, TimeBucketOption | null>,
  ) => void
  // Constraint props
  isDisabled?: boolean
  disabledTooltip?: string
  invalidBreakdowns?: BreakdownOption[]
  getInvalidReason?: (breakdown: BreakdownOption) => string | null
  // Per-breakdown grouping callback — receives the breakdown's array index and the new value
  onGroupingChange?: (breakdownIndex: number, enabled: boolean) => void
  // Custom breakdowns available for selection
  customBreakdowns?: CustomBreakdown[]
  // Label configuration
  labels?: {
    primary?: string
    secondary?: string
    tertiary?: string
  }
  // Card label mode
  cardLabelMode?: 'tooltip' | 'text'
  // Whether to show the model hierarchy prefix (e.g. "Post > Theme")
  showBreakdownHierarchy: boolean
  // Whether to show the small grey card label above the card content
  showCardLabels?: boolean
  // Optional cross-panel sync toggle, rendered in the header left of the add button.
  syncToggle?: React.ReactNode
}

export const BreakdownControl: React.FC<BreakdownControlProps> = ({
  selectedBreakdowns,
  setSelectedBreakdowns,
  selectedTimeBucket: _selectedTimeBucket,
  setSelectedTimeBucket: _setSelectedTimeBucket,
  maxSelections = 3,
  timeBucketSelections = {},
  setTimeBucketSelections,
  isDisabled = false,
  disabledTooltip,
  invalidBreakdowns = [],
  getInvalidReason,
  onGroupingChange,
  customBreakdowns = [],
  labels,
  cardLabelMode,
  showBreakdownHierarchy,
  showCardLabels = false,
  syncToggle,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  // Helper to get unique ID for any breakdown (standard or custom)
  const getBreakdownId = (breakdown: BreakdownSelection): string => {
    if (!breakdown) return 'none'

    if (isStandardBreakdown(breakdown)) {
      return breakdown.id
    } else if (isCustomBreakdown(breakdown)) {
      return getCustomBreakdownId(breakdown)
    }
    return 'none'
  }

  // Helper to check if breakdown is already selected
  const isBreakdownSelected = (breakdown: BreakdownSelection): boolean => {
    return selectedBreakdowns.some(
      (b) => getBreakdownId(b) === getBreakdownId(breakdown),
    )
  }

  // Handle drag end event for unified list
  const handleDragEnd = (event: DragEndEvent): void => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      const oldIndex = selectedBreakdowns.findIndex(
        (breakdown) => getBreakdownId(breakdown) === active.id,
      )
      const newIndex = selectedBreakdowns.findIndex(
        (breakdown) => getBreakdownId(breakdown) === over.id,
      )

      if (oldIndex !== -1 && newIndex !== -1) {
        setSelectedBreakdowns(arrayMove(selectedBreakdowns, oldIndex, newIndex))
      }
    }
  }

  // Handle breakdown selection - unified for both standard and custom
  const handleBreakdownSelect = (selection: BreakdownSelection): void => {
    if (!selection) {
      return
    }

    // Check max selections
    if (selectedBreakdowns.length >= maxSelections) {
      return
    }

    // Check if already selected
    if (isBreakdownSelected(selection)) {
      return
    }

    // Add to unified list
    setSelectedBreakdowns([...selectedBreakdowns, selection])
  }

  // Handle removing a breakdown selection
  const handleRemoveBreakdown = (breakdown: BreakdownSelection): void => {
    setSelectedBreakdowns(
      selectedBreakdowns.filter(
        (b) => getBreakdownId(b) !== getBreakdownId(breakdown),
      ),
    )

    // Also clean up time bucket selection if it's a standard breakdown
    if (
      breakdown &&
      isStandardBreakdown(breakdown) &&
      setTimeBucketSelections
    ) {
      const breakdownId = breakdown.id
      if (timeBucketSelections[breakdownId]) {
        const updatedSelections = { ...timeBucketSelections }
        delete updatedSelections[breakdownId]
        setTimeBucketSelections(updatedSelections)
      }
    }
  }

  // Handle changing a breakdown selection
  const handleBreakdownChange = (
    oldBreakdown: BreakdownSelection,
    newBreakdown: BreakdownSelection,
  ): void => {
    if (!newBreakdown) return

    // Check if the new breakdown is already selected elsewhere
    const newBreakdownId = getBreakdownId(newBreakdown)
    const oldBreakdownId = getBreakdownId(oldBreakdown)

    const isDuplicate = selectedBreakdowns.some((b) => {
      const bId = getBreakdownId(b)
      // Check if this breakdown matches newBreakdown but isn't the one being replaced
      return bId === newBreakdownId && bId !== oldBreakdownId
    })

    // If duplicate, reject the change
    if (isDuplicate) {
      return
    }

    // Replace old breakdown with new one in the list
    const updatedBreakdowns = selectedBreakdowns.map((b) =>
      getBreakdownId(b) === oldBreakdownId ? newBreakdown : b,
    )
    setSelectedBreakdowns(updatedBreakdowns)
  }

  // Handle time bucket change for a specific breakdown
  const handleTimeBucketChange = (
    breakdown: BreakdownSelection,
    timeBucket: TimeBucketOption | null,
  ): void => {
    if (
      setTimeBucketSelections &&
      breakdown &&
      isStandardBreakdown(breakdown)
    ) {
      const updatedSelections = {
        ...timeBucketSelections,
        [breakdown.id]: timeBucket,
      }
      setTimeBucketSelections(updatedSelections)
    }
  }

  // Get time bucket selection for a specific breakdown
  const getTimeBucketForBreakdown = (
    breakdown: BreakdownSelection,
  ): TimeBucketOption | null => {
    if (breakdown && isStandardBreakdown(breakdown)) {
      return timeBucketSelections[breakdown.id] || null
    }
    return null
  }

  // Convert any breakdown to FilterCard-compatible format
  const createBreakdownFilter = (
    breakdown: BreakdownSelection,
  ): FilterOptionSelect => {
    if (!breakdown) {
      return {
        field: 'none',
        alias: 'None',
        filter_type: FilterTypes.SELECT,
        values: [],
      }
    }

    if (isStandardBreakdown(breakdown)) {
      const breakdownConfig = Object.values(BREAKDOWNS).find(
        (b) => b.id === breakdown.id,
      )
      return {
        field: breakdown.id,
        alias: breakdownConfig?.label || breakdown.id,
        filter_type: FilterTypes.SELECT,
        values: [{ value: breakdown.id, n_posts: 0, identifiers: [] }],
      }
    } else if (isCustomBreakdown(breakdown)) {
      return {
        field: getCustomBreakdownId(breakdown),
        alias: breakdown.alias,
        filter_type: FilterTypes.SELECT,
        values: [{ value: breakdown.field, n_posts: 0, identifiers: [] }],
      }
    }

    return {
      field: 'none',
      alias: 'None',
      filter_type: FilterTypes.SELECT,
      values: [],
    }
  }

  const hasAnySelections = selectedBreakdowns.length > 0

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <BreakdownSelectHeader
        onSelect={handleBreakdownSelect}
        selectedBreakdown={undefined} // Don't show any as "selected" in the header since we show multiple below
        isDisabled={isDisabled}
        disabledTooltip={disabledTooltip}
        invalidBreakdowns={invalidBreakdowns}
        getInvalidReason={getInvalidReason}
        customBreakdowns={customBreakdowns}
        allSelectedBreakdowns={selectedBreakdowns}
        showBreakdownHierarchy={showBreakdownHierarchy}
        syncToggle={syncToggle}
      />

      {/* Render selected breakdowns (unified list with drag-and-drop) */}
      {hasAnySelections && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          }}
        >
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={selectedBreakdowns.map(getBreakdownId)}
              strategy={verticalListSortingStrategy}
            >
              {selectedBreakdowns.map((breakdown, index) => {
                if (!breakdown) return null

                const breakdownId = getBreakdownId(breakdown)
                const isCustom = isCustomBreakdown(breakdown)
                const label =
                  index === 0
                    ? labels?.primary
                    : index === 1
                      ? labels?.secondary
                      : index === 2
                        ? labels?.tertiary
                        : undefined

                return (
                  <BreakdownCard
                    mode="breakdown"
                    key={`${breakdownId}-${index}`}
                    id={breakdownId}
                    filter={createBreakdownFilter(breakdown)}
                    onRemove={() => handleRemoveBreakdown(breakdown)}
                    onBreakdownChange={(newBreakdown) =>
                      handleBreakdownChange(breakdown, newBreakdown)
                    }
                    currentBreakdown={
                      isStandardBreakdown(breakdown)
                        ? breakdown.id
                        : (breakdown.field as BreakdownOption)
                    }
                    selectedTimeBucket={getTimeBucketForBreakdown(breakdown)}
                    onTimeBucketChange={(timeBucket) =>
                      handleTimeBucketChange(breakdown, timeBucket)
                    }
                    invalidBreakdowns={invalidBreakdowns}
                    getInvalidReason={getInvalidReason}
                    showBrandGrouping={
                      isGroupableBreakdown(breakdown)
                        ? breakdown.grouped
                        : false
                    }
                    onBrandGroupingChange={
                      isGroupableBreakdown(breakdown)
                        ? (enabled) => onGroupingChange?.(index, enabled)
                        : undefined
                    }
                    customBreakdowns={customBreakdowns}
                    isCustomBreakdown={isCustom}
                    customBreakdownData={isCustom ? breakdown : undefined}
                    allSelectedBreakdowns={selectedBreakdowns}
                    label={label || undefined}
                    cardLabelMode={cardLabelMode}
                    showBreakdownHierarchy={showBreakdownHierarchy}
                    showCardLabels={showCardLabels}
                  />
                )
              })}
            </SortableContext>
          </DndContext>
        </div>
      )}
    </div>
  )
}
